<template>
  <form
    class="tenant-login-form"
    data-testid="tenant-login-form"
    novalidate
    @submit.prevent="onPasswordLogin"
  >
    <header class="tenant-login-form__header">
      <h1 class="tenant-login-form__title">{{ copy.SIGN_IN_HEADING }}</h1>
      <p class="tenant-login-form__subtitle">{{ copy.DISCOVER_SUBTITLE }}</p>
    </header>

    <AuthLoginAlert :message="bannerMessage" />

    <div v-if="localAllowed" class="tenant-login-form__fields">
      <div class="tenant-login-form__field">
        <label class="tenant-login-form__label" for="tenant-login-email">{{ copy.WORK_EMAIL_LABEL }}</label>
        <div
          class="tenant-login-form__control"
          :class="{
            'tenant-login-form__control--filled': Boolean(emailLocal),
            'tenant-login-form__control--error': Boolean(emailError),
          }"
        >
          <input
            id="tenant-login-email"
            v-model="emailLocal"
            class="tenant-login-form__input"
            type="email"
            name="email"
            autocomplete="username"
            :aria-invalid="Boolean(emailError)"
            :aria-describedby="emailError ? 'tenant-login-email-error' : null"
            :disabled="loading || ssoInProgress"
            data-testid="tenant-login-form__email"
          />
        </div>
        <p
          v-if="emailError"
          id="tenant-login-email-error"
          class="tenant-login-form__field-error"
          role="alert"
          data-testid="tenant-login-form__email-error"
        >
          {{ emailError }}
        </p>
      </div>

      <div class="tenant-login-form__field">
        <label class="tenant-login-form__label" for="tenant-login-password">Password</label>
        <div
          class="tenant-login-form__control"
          :class="{
            'tenant-login-form__control--filled': Boolean(password),
            'tenant-login-form__control--error': Boolean(passwordError),
          }"
        >
          <input
            id="tenant-login-password"
            v-model="password"
            class="tenant-login-form__input text-transform-none"
            :type="showPassword ? 'text' : 'password'"
            name="password"
            autocomplete="current-password"
            :aria-invalid="Boolean(passwordError)"
            :aria-describedby="passwordError ? 'tenant-login-password-error' : null"
            :disabled="loading || ssoInProgress"
            data-testid="tenant-login-form__password"
          />
          <button
            type="button"
            class="tenant-login-form__toggle"
            :disabled="loading || ssoInProgress"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
            data-testid="tenant-login-form__password-toggle"
            @click="showPassword = !showPassword"
          >
            <img
              class="tenant-login-form__toggle-icon"
              :src="showPassword ? '/img/icons/EyeIcon.svg' : '/img/auth/icon-eye-off.svg'"
              width="20"
              height="20"
              alt=""
            />
          </button>
        </div>
        <p
          v-if="passwordError"
          id="tenant-login-password-error"
          class="tenant-login-form__field-error"
          role="alert"
          data-testid="tenant-login-form__password-error"
        >
          {{ passwordError }}
        </p>
      </div>

      <div class="tenant-login-form__reset-row">
        <router-link
          to="/forgotpassword"
          class="tenant-login-form__reset"
          data-testid="tenant-login-form__reset-password"
        >
          Reset password
        </router-link>
      </div>
    </div>

    <button
      v-if="localAllowed"
      type="submit"
      class="tenant-login-form__login"
      :disabled="!canLocalLogin"
      data-testid="tenant-login-form__login"
    >
      Login
    </button>

    <div
      v-if="localAllowed && sortedIdps.length"
      class="tenant-login-form__divider"
      data-testid="tenant-login-form__divider"
    >
      <span class="tenant-login-form__divider-line" />
      <span class="tenant-login-form__divider-text">or continue with</span>
      <span class="tenant-login-form__divider-line" />
    </div>

    <div v-if="sortedIdps.length" class="tenant-login-form__idps">
      <button
        v-for="idp in sortedIdps"
        :key="idp.connectionId"
        type="button"
        class="tenant-login-form__idp"
        :disabled="loading || ssoInProgress"
        :data-testid="`tenant-login-form__idp--${idp.connectionId}`"
        @click="onSsoStart(idp)"
      >
        <img
          v-if="idpIconSrc(idp)"
          class="tenant-login-form__idp-icon"
          :src="idpIconSrc(idp)"
          width="16"
          height="16"
          alt=""
        />
        <span class="tenant-login-form__idp-label">{{ idp.displayName || 'Sign in with SSO' }}</span>
      </button>
    </div>

    <p v-if="!localAllowed && !sortedIdps.length" class="tenant-login-form__empty">
      Sign-in is not available for this workspace right now.
    </p>

    <button
      v-if="showBack"
      type="button"
      class="tenant-login-form__back"
      :disabled="loading || ssoInProgress"
      data-testid="tenant-login-form__back"
      @click="$emit('back')"
    >
      {{ copy.BACK }}
    </button>
  </form>
</template>

<script>
import constants from '../../../plugins/constants';
import AuthLoginAlert from './AuthLoginAlert.vue';
import { idpIconSrc } from './TenantLoginFormUtils';

const { AUTH } = constants;

export default {
  name: 'TenantLoginForm',
  components: {
    AuthLoginAlert,
  },
  props: {
    email: { type: String, default: '' },
    localAllowed: { type: Boolean, default: true },
    idps: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    ssoInProgress: { type: Boolean, default: false },
    showBack: { type: Boolean, default: false },
    alertMessage: { type: String, default: '' },
  },
  data() {
    return {
      emailLocal: this.email || '',
      password: '',
      showPassword: false,
      copy: AUTH,
    };
  },
  computed: {
    sortedIdps() {
      return [...(this.idps || [])].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
      );
    },
    canLocalLogin() {
      return Boolean(
        this.localAllowed &&
          this.emailLocal &&
          this.password &&
          !this.loading &&
          !this.ssoInProgress,
      );
    },
    emailError() {
      return this.alertMessage === this.copy.GENERIC_DISCOVER_ERROR
        ? this.alertMessage
        : '';
    },
    passwordError() {
      return this.alertMessage === this.copy.INVALID_EMAIL_OR_PASSWORD
        ? this.alertMessage
        : '';
    },
    bannerMessage() {
      return this.emailError || this.passwordError ? '' : this.alertMessage;
    },
  },
  watch: {
    email(value) {
      this.emailLocal = value || '';
    },
  },
  beforeDestroy() {
    this.emailLocal = '';
    this.password = '';
    this.showPassword = false;
  },
  methods: {
    getCredentials() {
      return { email: this.emailLocal, password: this.password };
    },
    idpIconSrc,
    onPasswordLogin() {
      if (!this.canLocalLogin) return;
      this.$emit('password-login', { email: this.emailLocal, password: this.password });
    },
    onSsoStart(idp) {
      if (this.loading || this.ssoInProgress) return;
      this.$emit('sso-start', idp);
    },
  },
};
</script>

<style lang="scss" scoped>
@import './TenantLoginForm.scss';
</style>
